// ---------------------------------------------------------------------------
// ICP → Apollo filter mapper
//
// Converts the user's plain-English Ideal Customer Profile description into
// structured Apollo People Search filters. Apollo's `q_keywords` is a fuzzy
// substring filter that matches across many fields (name, company, title, ...),
// so naïvely passing "CEOs" matches a company literally named "CEOS GmbH".
// We let an LLM pick out roles → `person_titles[]`, seniorities →
// `person_seniorities[]`, and leave only genuine residual industry/keyword
// terms in `q_keywords`.
//
// `@adornis/llm/server/...` is a server-only path per Engine convention, so
// importing it from this module keeps the LLM SDK out of any client bundle
// that ends up consuming `@ndi/leads`.
// ---------------------------------------------------------------------------

import { createHash } from 'node:crypto';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { AdornisLLMRouter } from '@adornis/llm/server/llm-router.js';
import type { ApolloLeadSearchInput } from '../baseql/apollo-lead-search.js';

// Apollo's documented seniority enum.
export type ApolloSeniority =
  | 'owner'
  | 'founder'
  | 'c_suite'
  | 'partner'
  | 'vp'
  | 'head'
  | 'director'
  | 'manager'
  | 'senior'
  | 'entry'
  | 'intern';

export interface IcpMapping {
  /** Canonical job titles to feed into Apollo's `person_titles[]`. */
  personTitles: string[];
  /** Seniority buckets from Apollo's allowed enum. */
  personSeniorities: ApolloSeniority[];
  /**
   * Person locations (cities, regions, countries) extracted from the ICP
   * description. Merged into Apollo's `person_locations[]`.
   */
  personLocations: string[];
  /**
   * Company/organisation locations extracted from the ICP description.
   * Merged into Apollo's `organization_locations[]`.
   */
  organizationLocations: string[];
  /**
   * Industry or technology keywords extracted from the ICP description.
   * Goes into Apollo's `q_organization_keyword_tags[]`.
   */
  organizationIndustryKeywords: string[];
  /**
   * Employee count ranges extracted from the ICP description (e.g. "headcount
   * between 100 and 5000"). Apollo range format "min,max". Merged into
   * Apollo's `organization_num_employees_ranges[]`.
   */
  employeeRanges: string[];
  /**
   * Functional departments of the target contacts, restricted to the UI's
   * department dropdown values. Not an Apollo filter itself — used to fill the
   * Department form field and as an input signal on subsequent mappings.
   */
  departments: string[];
  /** Annual company revenue range in USD extracted from the ICP, or null. */
  revenueMinUsd: number | null;
  revenueMaxUsd: number | null;
  /**
   * Residual free-text terms that don't map to any structured filter. Goes
   * into `q_keywords`. Should be empty for most well-formed ICP inputs.
   */
  qKeywords: string;
  /**
   * True when the LLM mapping failed and this mapping is only the raw-text
   * fallback. Never cached — surfaced to the UI so the user can retry.
   */
  mappingFailed?: boolean;
}

const ALLOWED_SENIORITIES: ReadonlySet<ApolloSeniority> = new Set([
  'owner',
  'founder',
  'c_suite',
  'partner',
  'vp',
  'head',
  'director',
  'manager',
  'senior',
  'entry',
  'intern',
]);

// Must stay in sync with the DEPARTMENTS dropdown in wiz-leads.ts.
const ALLOWED_DEPARTMENTS: ReadonlySet<string> = new Set([
  'C-Suite',
  'Engineering',
  'Finance',
  'Human Resources',
  'IT',
  'Legal',
  'Marketing',
  'Operations',
  'Product',
  'Sales',
]);

const SYSTEM_PROMPT = `You translate a user's lead-sourcing intent into Apollo People Search filters.

You receive a JSON object with these (any may be empty):
- icpDescription: free-text Ideal Customer Profile.
- jobTitle: comma-separated explicit titles the user already typed.
- department: comma-separated dropdown values naming functions (e.g. Sales, Marketing, Engineering, Finance, Operations, C-Suite). "C-Suite" is a seniority signal: map it to personSeniorities ["c_suite"] and matching executive titles rather than a functional department.
- companyIndustry: comma-separated dropdown values naming industries (e.g. Financial Services, Manufacturing, SaaS).
- industryKeywords: free-text industry / technology keywords.

Output a single JSON object — no prose, no markdown fences — with exactly these keys:

- "personTitles": string[] — canonical job titles to feed Apollo's person_titles[]. Expand acronyms and plurals into full forms (e.g. "CEOs" → ["CEO","Chief Executive Officer"]). Translate department + ICP signals into matching titles (e.g. department "Sales" + ICP "leaders" → ["Head of Sales","VP Sales","Sales Director","Chief Revenue Officer"]). IMPORTANT: when the ICP describes target COMPANIES but names no person or role (e.g. "companies that sell X"), default to the companies' decision makers: personTitles ["CEO","Chief Executive Officer","Managing Director","Founder","Owner"], personSeniorities ["c_suite","owner","founder"], departments ["C-Suite"] — never leave all three empty, otherwise the search returns every employee. DO NOT include the user's explicit jobTitle entries; the caller unions them in.
- "personSeniorities": string[] — zero or more values from this exact enum: ["owner","founder","c_suite","partner","vp","head","director","manager","senior","entry","intern"]. Map roles to seniorities (e.g. CEO/CFO/CTO → "c_suite"; VP of X → "vp"; Head of X → "head"). Empty if undetermined. IMPORTANT: seniorities are AND-combined with personTitles, so a wrong bucket silently removes nearly all matches. Only emit seniorities the ICP actually implies (leadership wording, "senior", "junior", C-level roles). For rank-and-file professions with no seniority signal — teachers, nurses, engineers, developers, consultants, accountants, recruiters — return [].
- "personLocations": string[] — cities, regions, or countries that describe WHERE THE PERSON is located. Use Apollo-compatible place names (cities, US states, or country names in English). Examples: "munich" → ["Munich, Bavaria, Germany"]; "bay area" → ["San Francisco, California, United States","Oakland, California, United States","San Jose, California, United States"]; "DACH" → ["Germany","Austria","Switzerland"]. Empty array if no person location is mentioned.
- "organizationLocations": string[] — cities, regions, or countries describing WHERE THE COMPANY is located/headquartered. Same format as personLocations. Attribute the location to the entity it grammatically belongs to: "CEOs based in Munich" → personLocations; "kitchen studios near London" / "companies in Ireland" / "firms with HQ in Germany" → organizationLocations (the location modifies the COMPANY). When genuinely ambiguous, prefer organizationLocations for company-type ICPs and personLocations for person-type ICPs.
- "organizationIndustryKeywords": string[] — industry sectors, verticals, or technology domains extracted from companyIndustry + industryKeywords + ICP wording (e.g. "chemistry" → ["chemistry"]; "fintech" → ["fintech"]; "SaaS" → ["saas"]). When the ICP names a business type — however niche — ALWAYS derive keywords from it. Use SPECIFIC compound keywords and avoid bare single words that also match adjacent sectors: plain "kitchen" matches restaurants and caterers — use ["kitchen studio","kitchen retailer","kitchen showroom","kitchen furniture"] instead; plain "dental" matches dentists — for labs use ["dental laboratory","dental technology"]; plain "brewing" matches coffee roasters and tea houses (coffee is "brewed") — for beer producers use ["craft brewery","microbrewery","brewpub"], never bare "brewing" or "beverage". Do NOT include role names, seniorities, or location terms here. Empty array only when no business type is mentioned at all.
- "employeeRanges": string[] — company employee-count (headcount) ranges extracted from icpDescription, each formatted as "min,max" with plain integers. Examples: "headcount between 100 and 5000" → ["100,5000"]; "more than 500 employees" → ["501,1000000"]; "up to 200 employees" → ["1,200"]; "SMEs"/"Mittelstand" → ["10,999"]; "enterprise" → ["1000,1000000"]. Empty array if no company size is mentioned.
- "departments": string[] — functional areas of the TARGET CONTACTS, zero or more values from this exact enum: ["C-Suite","Engineering","Finance","Human Resources","IT","Legal","Marketing","Operations","Product","Sales"]. C-level / Managing Director / decision-maker ICPs → ["C-Suite"]. Empty array if undetermined.
- "revenueMinUsd" / "revenueMaxUsd": number or null — annual company revenue range in plain USD extracted from icpDescription. Examples: "revenue between 50M and 1B" → 50000000 / 1000000000; "more than €100M revenue" → 100000000 / null. null when revenue is not mentioned.
- "qKeywords": string — residual descriptive terms that don't fit any of the above structured filters. Should be empty for most inputs. Never put role names, seniorities, locations, industry terms, employee counts / headcount, or revenue here.

Examples:

INPUT: {"icpDescription":"CEOs","companyIndustry":"Manufacturing"}
{"personTitles":["CEO","Chief Executive Officer","Managing Director"],"personSeniorities":["c_suite","owner","founder"],"personLocations":[],"organizationLocations":[],"organizationIndustryKeywords":["manufacturing"],"employeeRanges":[],"departments":["C-Suite"],"revenueMinUsd":null,"revenueMaxUsd":null,"qKeywords":""}

INPUT: {"icpDescription":"Heads of marketing","companyIndustry":"Financial Services","industryKeywords":"fintech"}
{"personTitles":["Head of Marketing","Marketing Director","VP Marketing","CMO"],"personSeniorities":["head","vp","director","c_suite"],"personLocations":[],"organizationLocations":[],"organizationIndustryKeywords":["financial services","fintech"],"employeeRanges":[],"departments":["Marketing"],"revenueMinUsd":null,"revenueMaxUsd":null,"qKeywords":""}

INPUT: {"department":"Sales","industryKeywords":"Kubernetes"}
{"personTitles":["VP Sales","Head of Sales","Sales Director","Account Executive","Chief Revenue Officer"],"personSeniorities":["vp","head","director","c_suite"],"personLocations":[],"organizationLocations":[],"organizationIndustryKeywords":["kubernetes"],"employeeRanges":[],"departments":["Sales"],"revenueMinUsd":null,"revenueMaxUsd":null,"qKeywords":""}

INPUT: {"icpDescription":"CEOs in the german chemistry industry in the munich area"}
{"personTitles":["CEO","Chief Executive Officer","Managing Director","Geschäftsführer"],"personSeniorities":["c_suite","owner","founder"],"personLocations":["Munich, Bavaria, Germany"],"organizationLocations":["Germany"],"organizationIndustryKeywords":["chemistry"],"employeeRanges":[],"departments":["C-Suite"],"revenueMinUsd":null,"revenueMaxUsd":null,"qKeywords":""}

INPUT: {"icpDescription":"CEOs in the american tech industry in the bay area"}
{"personTitles":["CEO","Chief Executive Officer","Managing Director"],"personSeniorities":["c_suite","owner","founder"],"personLocations":["San Francisco, California, United States","Oakland, California, United States","San Jose, California, United States"],"organizationLocations":["United States"],"organizationIndustryKeywords":["technology","tech","software"],"employeeRanges":[],"departments":["C-Suite"],"revenueMinUsd":null,"revenueMaxUsd":null,"qKeywords":""}

INPUT: {"icpDescription":"CEOs in retail companies","companyIndustry":"Retail"}
{"personTitles":["CEO","Chief Executive Officer","Managing Director"],"personSeniorities":["c_suite","owner","founder"],"personLocations":[],"organizationLocations":[],"organizationIndustryKeywords":["retail"],"employeeRanges":[],"departments":["C-Suite"],"revenueMinUsd":null,"revenueMaxUsd":null,"qKeywords":""}

INPUT: {"icpDescription":"Heads of marketing in advertising agencies","companyIndustry":"Advertising & Marketing"}
{"personTitles":["Head of Marketing","Marketing Director","VP Marketing","CMO"],"personSeniorities":["head","vp","director","c_suite"],"personLocations":[],"organizationLocations":[],"organizationIndustryKeywords":["advertising","marketing"],"employeeRanges":[],"departments":["Marketing"],"revenueMinUsd":null,"revenueMaxUsd":null,"qKeywords":""}

INPUT: {"icpDescription":"CFOs in banking institutions","companyIndustry":"Banking"}
{"personTitles":["CFO","Chief Financial Officer","Finance Director","VP Finance"],"personSeniorities":["c_suite","vp","director"],"personLocations":[],"organizationLocations":[],"organizationIndustryKeywords":["banking","finance"],"employeeRanges":[],"departments":["Finance","C-Suite"],"revenueMinUsd":null,"revenueMaxUsd":null,"qKeywords":""}

INPUT: {"icpDescription":"Engineering managers at software companies","companyIndustry":"Software","industryKeywords":"enterprise software"}
{"personTitles":["Engineering Manager","VP Engineering","Head of Engineering","CTO"],"personSeniorities":["manager","vp","head","c_suite"],"personLocations":[],"organizationLocations":[],"organizationIndustryKeywords":["software","enterprise software"],"employeeRanges":[],"departments":["Engineering"],"revenueMinUsd":null,"revenueMaxUsd":null,"qKeywords":""}

INPUT: {"icpDescription":"HR directors in healthcare services","companyIndustry":"Healthcare Services"}
{"personTitles":["HR Director","Head of HR","VP Human Resources","CHRO"],"personSeniorities":["director","head","vp","c_suite"],"personLocations":[],"organizationLocations":[],"organizationIndustryKeywords":["healthcare","healthcare services"],"employeeRanges":[],"departments":["Human Resources"],"revenueMinUsd":null,"revenueMaxUsd":null,"qKeywords":""}

INPUT: {"icpDescription":"Sales leaders in manufacturing companies","companyIndustry":"Manufacturing"}
{"personTitles":["VP Sales","Head of Sales","Sales Director","Chief Revenue Officer"],"personSeniorities":["vp","head","director","c_suite"],"personLocations":[],"organizationLocations":[],"organizationIndustryKeywords":["manufacturing"],"employeeRanges":[],"departments":["Sales"],"revenueMinUsd":null,"revenueMaxUsd":null,"qKeywords":""}

INPUT: {"icpDescription":"IT managers in educational institutions","companyIndustry":"Education"}
{"personTitles":["IT Manager","Director of IT","VP of Technology","CTO"],"personSeniorities":["manager","director","vp","c_suite"],"personLocations":[],"organizationLocations":[],"organizationIndustryKeywords":["education","educational technology"],"employeeRanges":[],"departments":["IT"],"revenueMinUsd":null,"revenueMaxUsd":null,"qKeywords":""}

INPUT: {"icpDescription":"Managing Directors of manufacturing companies with HQ in germany, headcount between 100 and 5000"}
{"personTitles":["Managing Director","Geschäftsführer","CEO","Chief Executive Officer"],"personSeniorities":["c_suite","owner","founder"],"personLocations":[],"organizationLocations":["Germany"],"organizationIndustryKeywords":["manufacturing"],"employeeRanges":["100,5000"],"departments":["C-Suite"],"revenueMinUsd":null,"revenueMaxUsd":null,"qKeywords":""}

INPUT: {"icpDescription":"Decision makers at Dutch logistics companies with annual revenue between 50M and 1B USD"}
{"personTitles":["CEO","Chief Executive Officer","COO","Chief Operating Officer","Managing Director"],"personSeniorities":["c_suite","owner","founder"],"personLocations":[],"organizationLocations":["Netherlands"],"organizationIndustryKeywords":["logistics"],"employeeRanges":[],"departments":["C-Suite"],"revenueMinUsd":50000000,"revenueMaxUsd":1000000000,"qKeywords":""}

INPUT: {"icpDescription":"CEOs of small kitchen studios near London"}
{"personTitles":["CEO","Chief Executive Officer","Managing Director","Founder","Owner"],"personSeniorities":["c_suite","owner","founder"],"personLocations":[],"organizationLocations":["London, England, United Kingdom"],"organizationIndustryKeywords":["kitchen studio","kitchen showroom","kitchen furniture"],"employeeRanges":["1,50"],"departments":["C-Suite"],"revenueMinUsd":null,"revenueMaxUsd":null,"qKeywords":""}

INPUT: {"icpDescription":"Companies that sell kitchens to customers in Ireland"}
{"personTitles":["CEO","Chief Executive Officer","Managing Director","Founder","Owner"],"personSeniorities":["c_suite","owner","founder"],"personLocations":[],"organizationLocations":["Ireland"],"organizationIndustryKeywords":["kitchen retailer","kitchen studio","kitchen showroom","kitchen furniture"],"employeeRanges":[],"departments":["C-Suite"],"revenueMinUsd":null,"revenueMaxUsd":null,"qKeywords":""}

INPUT: {"icpDescription":"Owners of craft breweries in Bavaria"}
{"personTitles":["Owner","Managing Director","Founder","CEO"],"personSeniorities":["owner","founder","c_suite"],"personLocations":[],"organizationLocations":["Bavaria, Germany"],"organizationIndustryKeywords":["craft brewery","microbrewery","brewpub"],"employeeRanges":[],"departments":["C-Suite"],"revenueMinUsd":null,"revenueMaxUsd":null,"qKeywords":""}

INPUT: {"icpDescription":"Secondary school teachers in Germany"}
{"personTitles":["Teacher","Secondary School Teacher","Secondary Education Teacher","Gymnasiallehrer"],"personSeniorities":[],"personLocations":["Germany"],"organizationLocations":["Germany"],"organizationIndustryKeywords":["secondary education","education"],"employeeRanges":[],"departments":[],"revenueMinUsd":null,"revenueMaxUsd":null,"qKeywords":""}`;

interface RawMapping {
  personTitles?: unknown;
  personSeniorities?: unknown;
  personLocations?: unknown;
  organizationLocations?: unknown;
  organizationIndustryKeywords?: unknown;
  employeeRanges?: unknown;
  departments?: unknown;
  revenueMinUsd?: unknown;
  revenueMaxUsd?: unknown;
  qKeywords?: unknown;
}

const sanitizeStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const item of value) {
    if (typeof item !== 'string') continue;
    const trimmed = item.trim();
    if (trimmed) out.push(trimmed);
  }
  return out;
};

const sanitizeSeniorities = (value: unknown): ApolloSeniority[] => {
  return sanitizeStringArray(value)
    .map(v => v.toLowerCase())
    .filter((v): v is ApolloSeniority => ALLOWED_SENIORITIES.has(v as ApolloSeniority));
};

// Apollo expects "min,max" with plain integers, min <= max.
const sanitizeEmployeeRanges = (value: unknown): string[] => {
  const out: string[] = [];
  for (const item of sanitizeStringArray(value)) {
    const match = item.match(/^(\d+)\s*,\s*(\d+)$/);
    if (!match) continue;
    const min = Number(match[1]);
    const max = Number(match[2]);
    if (min > max) continue;
    out.push(`${min},${max}`);
  }
  return out;
};

const sanitizeDepartments = (value: unknown): string[] => {
  const byLower = new Map([...ALLOWED_DEPARTMENTS].map(d => [d.toLowerCase(), d]));
  return [...new Set(sanitizeStringArray(value).map(v => byLower.get(v.toLowerCase())))].filter(
    (v): v is string => !!v,
  );
};

/**
 * Drop bare single-word industry keywords when a more specific compound with
 * the same word exists in the list. Apollo ORs keyword tags, so a bare
 * "furniture" next to "furniture retailer" silently re-widens the search to
 * every furniture manufacturer — the compound alone keeps it precise.
 */
const dropRedundantBareKeywords = (keywords: string[]): string[] => {
  return keywords.filter(k => {
    if (k.includes(' ')) return true; // compounds always stay
    const word = k.toLowerCase();
    const containedInCompound = keywords.some(other => {
      if (other === k || !other.includes(' ')) return false;
      return other
        .toLowerCase()
        .split(/\s+/)
        .some(part => part === word || part === `${word}s` || `${part}s` === word);
    });
    return !containedInCompound;
  });
};

const sanitizeRevenue = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null;
  return Math.round(value);
};

const parseLlmJson = (text: string): RawMapping | null => {
  const cleaned = text
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/i, '')
    .trim();
  // Try a strict parse first, then fall back to grabbing the first {...} block.
  try {
    return JSON.parse(cleaned) as RawMapping;
  } catch {
    const match = cleaned.match(/\{[\s\S]*\}/);
    if (!match) return null;
    try {
      return JSON.parse(match[0]) as RawMapping;
    } catch {
      return null;
    }
  }
};

const EMPTY_MAPPING: IcpMapping = {
  personTitles: [],
  personSeniorities: [],
  personLocations: [],
  organizationLocations: [],
  organizationIndustryKeywords: [],
  employeeRanges: [],
  departments: [],
  revenueMinUsd: null,
  revenueMaxUsd: null,
  qKeywords: '',
};

/**
 * Build the JSON payload we hand the LLM. Only includes fields the user
 * actually filled in, so the model isn't distracted by empty strings.
 */
export const buildMapperPayload = (input: ApolloLeadSearchInput): Record<string, string> => {
  const payload: Record<string, string> = {};
  const stringFields: [keyof ApolloLeadSearchInput, string][] = [
    ['icpDescription', 'icpDescription'],
    ['jobTitle', 'jobTitle'],
    ['industryKeywords', 'industryKeywords'],
  ];
  for (const [key, label] of stringFields) {
    const value = (input[key] as string | undefined)?.trim();
    if (value) payload[label] = value;
  }
  // Array fields: join with commas so the LLM sees them as multi-select values.
  const arrayFields: [keyof ApolloLeadSearchInput, string][] = [
    ['department', 'department'],
    ['companyIndustry', 'companyIndustry'],
  ];
  for (const [key, label] of arrayFields) {
    const arr = input[key] as string[] | undefined;
    if (arr?.length) {
      const joined = arr
        .map(v => v.trim())
        .filter(Boolean)
        .join(', ');
      if (joined) payload[label] = joined;
    }
  }
  return payload;
};

const fallbackKeywords = (input: ApolloLeadSearchInput): string => {
  const industries = Array.isArray(input.companyIndustry)
    ? input.companyIndustry.join(' ')
    : (input.companyIndustry ?? '');
  return [input.icpDescription, industries, input.industryKeywords]
    .map(v => (typeof v === 'string' ? v.trim() : ''))
    .filter(Boolean)
    .join(' ');
};

// ---------------------------------------------------------------------------
// Persistent mapping cache
//
// LLM output varies between calls even at temperature 0, so re-running the
// mapper on IDENTICAL user input produced different Apollo filters — and with
// them wildly different lead counts (tester: 1,800 vs 4,081 for the same
// criteria). Cache the sanitized mapping in Mongo keyed by a hash of the
// payload: identical criteria → identical filters → consistent counts, across
// server restarts and recreated campaigns. The prompt version is part of the
// key so prompt changes invalidate old entries.
// ---------------------------------------------------------------------------

const MAPPING_CACHE_COLLECTION = 'icp_mapping_cache';
const MAPPING_PROMPT_VERSION = 9; // bump when SYSTEM_PROMPT or sanitizers change

export const mappingCacheKey = (payload: Record<string, string>): string => {
  const canonical = JSON.stringify(
    Object.keys(payload)
      .sort()
      .map(k => [k, payload[k]]),
  );
  return createHash('sha256').update(`v${MAPPING_PROMPT_VERSION}:${canonical}`).digest('hex');
};

const readCachedMapping = async (key: string): Promise<IcpMapping | null> => {
  try {
    const col = await getRawCollection(MAPPING_CACHE_COLLECTION);
    const doc = await col.findOne({ _id: key as never });
    if (!doc?.mappingJson) return null;
    const parsed = JSON.parse(doc.mappingJson as string) as IcpMapping;
    // Shape guard — stale/corrupt entries fall through to a fresh LLM call.
    if (!Array.isArray(parsed.personTitles) || typeof parsed.qKeywords !== 'string') return null;
    // Older entries may miss newer keys — normalize with defaults.
    return { ...EMPTY_MAPPING, ...parsed };
  } catch (err) {
    console.warn('[icp-mapper] cache read failed — continuing without cache', err);
    return null;
  }
};

const writeCachedMapping = async (key: string, payload: Record<string, string>, mapping: IcpMapping): Promise<void> => {
  try {
    const col = await getRawCollection(MAPPING_CACHE_COLLECTION);
    await col.updateOne(
      { _id: key as never },
      {
        $set: {
          payloadJson: JSON.stringify(payload),
          mappingJson: JSON.stringify(mapping),
          promptVersion: MAPPING_PROMPT_VERSION,
          updatedAt: new Date().toISOString(),
        },
        $setOnInsert: { createdAt: new Date().toISOString() },
      },
      { upsert: true },
    );
  } catch (err) {
    console.warn('[icp-mapper] cache write failed — mapping not persisted', err);
  }
};

export const mapIcpToApolloFilters = async (
  input: ApolloLeadSearchInput,
  opts?: { forceRemap?: boolean },
): Promise<IcpMapping> => {
  const payload = buildMapperPayload(input);
  // Nothing semantic to interpret — skip the LLM entirely.
  if (Object.keys(payload).length === 0) return EMPTY_MAPPING;

  // Identical input → reuse the persisted mapping (deterministic lead counts).
  // `forceRemap` skips the read (user-requested re-interpretation) but still
  // writes, so the fresh mapping becomes the new deterministic baseline.
  const cacheKey = mappingCacheKey(payload);
  if (!opts?.forceRemap) {
    const cached = await readCachedMapping(cacheKey);
    if (cached) {
      console.log('[icp-mapper] cache hit', { cacheKey });
      return cached;
    }
  }

  const llm = new AdornisLLMRouter({
    provider: 'openrouter',
    model: '~anthropic/claude-haiku-latest',
    temperature: 0,
    // The output holds up to ~7 string arrays; 512 was tight enough that long
    // interpretations got truncated mid-JSON and silently degraded into the
    // raw-keyword fallback search.
    max_tokens: 1024,
  });

  let text = '';
  try {
    const response = await llm.chat.completions.create({
      messages: [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: JSON.stringify(payload) },
      ],
    });
    text = response.choices[0]?.message?.content?.trim() ?? '';
  } catch (err) {
    console.warn('[icp-mapper] LLM call failed, falling back to raw keywords', err);
    return { ...EMPTY_MAPPING, qKeywords: fallbackKeywords(input), mappingFailed: true };
  }

  const parsed = parseLlmJson(text);
  if (!parsed) {
    console.warn('[icp-mapper] could not parse LLM JSON, falling back to raw keywords', { text });
    return { ...EMPTY_MAPPING, qKeywords: fallbackKeywords(input), mappingFailed: true };
  }

  const mapping: IcpMapping = {
    personTitles: sanitizeStringArray(parsed.personTitles),
    personSeniorities: sanitizeSeniorities(parsed.personSeniorities),
    personLocations: sanitizeStringArray(parsed.personLocations),
    organizationLocations: sanitizeStringArray(parsed.organizationLocations),
    organizationIndustryKeywords: dropRedundantBareKeywords(sanitizeStringArray(parsed.organizationIndustryKeywords)),
    employeeRanges: sanitizeEmployeeRanges(parsed.employeeRanges),
    departments: sanitizeDepartments(parsed.departments),
    revenueMinUsd: sanitizeRevenue(parsed.revenueMinUsd),
    revenueMaxUsd: sanitizeRevenue(parsed.revenueMaxUsd),
    qKeywords: typeof parsed.qKeywords === 'string' ? parsed.qKeywords.trim() : '',
  };

  await writeCachedMapping(cacheKey, payload, mapping);
  console.log('[icp-mapper]', { payload, mapping });
  return mapping;
};
